import { type ProjectEmployeeDayCard } from "@/lib/backend/api";

/**
 * The values you can filter Ontdek by, counted from what the window actually brought back.
 *
 * Function and city are not filter keys on `/project/employee-day-range` — `ProjectFilterKeys` has
 * `name`, `startDate`, `isFeatured` and little else. But both sit on every card in the response, so
 * the honest move is to filter over the window rather than pretend the server can do it.
 *
 * That is only truthful because the window _is_ the period being looked at: the endpoint returns
 * everything between its two dates and ignores pagination, so nothing is silently cut off before
 * this runs. Filtering over an arbitrarily truncated set would produce "niets gevonden" for things
 * that exist — the same shape of bug the hours badge had.
 */
export type DiscoverFacet = { value: string; count: number };

export type DiscoverFilters = {
  /** A function name from `availableFunctions`, or null for all. */
  userFunction: string | null;
  /** A city from `city`, or null for all. */
  city: string | null;
};

export const EMPTY_DISCOVER_FILTERS: DiscoverFilters = { userFunction: null, city: null };

/**
 * Counts per value, most common first, then alphabetical.
 *
 * Counted over the cards that survive the _other_ filters, so the numbers describe what you would
 * actually get — picking a city then shows how many of that city's days match your function, not
 * how many exist in total.
 */
function facetsFrom(
  cards: ProjectEmployeeDayCard[],
  pick: (card: ProjectEmployeeDayCard) => string[],
) {
  const counts = new Map<string, number>();

  for (const card of cards) {
    for (const value of new Set(pick(card))) {
      counts.set(value, (counts.get(value) ?? 0) + 1);
    }
  }

  return [...counts.entries()]
    .map(([value, count]): DiscoverFacet => ({ value, count }))
    .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
}

function matchesFunction(card: ProjectEmployeeDayCard, userFunction: string | null): boolean {
  return !userFunction || (card.availableFunctions ?? []).includes(userFunction);
}

function matchesCity(card: ProjectEmployeeDayCard, city: string | null): boolean {
  return !city || card.city === city;
}

/** The cards that survive every active filter. */
export function applyDiscoverFilters(
  cards: ProjectEmployeeDayCard[],
  filters: DiscoverFilters,
): ProjectEmployeeDayCard[] {
  return cards.filter(
    (card) => matchesFunction(card, filters.userFunction) && matchesCity(card, filters.city),
  );
}

/**
 * The options to offer, each counted against the other filter rather than against everything.
 *
 * A city list that still offers "Tilburg — 4" while your function filter rules all four out is a
 * list of dead ends.
 */
export function discoverFacets(cards: ProjectEmployeeDayCard[], filters: DiscoverFilters) {
  return {
    functions: facetsFrom(
      cards.filter((card) => matchesCity(card, filters.city)),
      (card) => card.availableFunctions ?? [],
    ),
    cities: facetsFrom(
      cards.filter((card) => matchesFunction(card, filters.userFunction)),
      (card) => (card.city ? [card.city] : []),
    ),
  };
}

/** How many filters are on — for the "wis filters" affordance and the empty state. */
export function activeFilterCount(filters: DiscoverFilters): number {
  return Number(!!filters.userFunction) + Number(!!filters.city);
}
